import {
  Activity,
  BarChart3,
  BellRing,
  CalendarDays,
  FileSignature,
  FileText,
  Globe,
  History,
  LayoutDashboard,
  Mail,
  Phone,
  ShieldCheck,
  Smartphone,
  UserCircle,
  CreditCard,
  type LucideIcon,
} from 'lucide-react';

export interface ShellNavItem {
  to: string;
  /** i18n key — the PRD forbids hard-coded user-facing copy. */
  labelKey: string;
  icon: LucideIcon;
  end?: boolean;
}

/**
 * The desktop rail.
 *
 * It began as the four destinations the reference build carries, matched to the
 * phone's bottom bar so that no screen could exist at 1440px and vanish at
 * 375px. The two have since had to differ, and the differences are the point
 * rather than drift: the phone gave its second slot to the send history and put
 * the gallery on the account screen, because a gallery is set up once and the
 * history is checked between jobs. A rail has room for both, so it carries
 * both — the send history was reachable on a phone and nowhere on a desktop,
 * which is the asymmetry the matching was meant to prevent in the first place.
 */
export const PRIMARY_NAV: ShellNavItem[] = [
  { to: '/inquiries', labelKey: 'shell.navLeads', icon: Smartphone },
  { to: '/calendar', labelKey: 'calendar.title', icon: CalendarDays },
  { to: '/quotes', labelKey: 'shell.sendHistory', icon: History },
  { to: '/mini-site', labelKey: 'shell.navGallery', icon: Globe },
  { to: '/invoices', labelKey: 'shell.navInvoicesEmail', icon: Mail },
  { to: '/customer-payments', labelKey: 'customerPayments.title', icon: CreditCard },
  { to: '/profile', labelKey: 'shell.navPersonalArea', icon: UserCircle },
];

/** Where the "+" sits in the rail: after inquiries, calendar, history and gallery. */
export const PRIMARY_NAV_SPLIT = 4;

/**
 * Screens Worki has and the reference build does not. They are not rows of the
 * rail — ten rows is a directory, not a menu.
 *
 * The account screen used to list them all under "more screens"; that card was
 * dropped, so most of these are now reached the way they are actually used —
 * the brand mark leads home, "+" opens the four things this product writes
 * down, the account screen links the gallery and the reports. `/calls` and
 * `/activity` are the two with no row of their own at the moment; both are
 * still routes, and both are one link away from being on this screen again.
 */
export const SECONDARY_NAV: ShellNavItem[] = [
  { to: '/', labelKey: 'nav.dashboard', icon: LayoutDashboard, end: true },
  { to: '/quotes', labelKey: 'quotes.title', icon: FileText },
  { to: '/signing', labelKey: 'signing.title', icon: FileSignature },
  { to: '/calls', labelKey: 'calls.title', icon: Phone },
  { to: '/reminders', labelKey: 'nav.reminders', icon: BellRing },
  { to: '/reports', labelKey: 'reports.title', icon: BarChart3 },
  { to: '/activity', labelKey: 'nav.activity', icon: Activity },
];

/**
 * Worki's own back office. Not a screen of the product the rail belongs to,
 * which is why it is the one row drawn from an answer rather than from a list:
 * `GET /platform/me`. The phone reaches it from the account screen instead —
 * four slots and a raised button leave no room for a fifth.
 */
export const PLATFORM_NAV: ShellNavItem = { to: '/platform', labelKey: 'nav.platform', icon: ShieldCheck };

/*
 * What the "+" opens, in both the rail and the bottom bar: things this product
 * writes down, and nothing else.
 *
 * The send history was here and is not a thing anyone writes — it is a place
 * they go, and it now has a row of its own on both widths. A new lead went with
 * it for the opposite reason: leads arrive, they are not composed. Both were
 * drawn muted, which was the menu admitting they did not belong.
 */
export const CREATE_ACTIONS: Array<ShellNavItem & { tone?: 'muted' }> = [
  { to: '/quotes/new', labelKey: 'dashboard.newQuote', icon: FileText },
  { to: '/signing/new', labelKey: 'signing.new', icon: FileSignature, tone: 'muted' },
  { to: '/reminders?new=1', labelKey: 'dashboard.newReminder', icon: BellRing, tone: 'muted' },
];

/**
 * Two slots each side of the raised button.
 * The calendar now takes the inquiries slot; inquiries stay in the personal area.
 *
 * The gallery used to hold the second slot and now lives on the account
 * screen: on a phone it is a screen an owner sets up once and then rarely
 * opens, while the history of what was sent is the thing they check between
 * jobs — so the slot went to the one they reach for.
 *
 * Like the rail's row, it points at the bare `/quotes` rather than at a tab.
 * The menus used to open the screen already narrowed to what had been sent,
 * and from inside that is indistinguishable from a filter someone left applied
 * last time: rows are missing and nothing says the screen was born that way.
 * All three of its filters default to "all" on their own, so the menus now
 * just open it.
 */
export const BOTTOM_NAV_START: ShellNavItem[] = [
  { to: '/calendar', labelKey: 'calendar.title', icon: CalendarDays },
  { to: '/quotes', labelKey: 'shell.sendHistoryShort', icon: History },
];

export const BOTTOM_NAV_END: ShellNavItem[] = [
  { to: '/invoices', labelKey: 'shell.navInvoicesShort', icon: Mail },
];

/**
 * 4.10 — addresses drawn over a screen their path is not under.
 *
 * A new document opens over the send history (`App.tsx`), at an address under
 * `/signing` — the list it used to open over. Read off the path, no row of
 * either menu was lit while it was open, and on a phone the sheet hides the
 * screen behind it, so the lit row is what is left to say which screen that is.
 *
 * Only the menus read this. The bar above a popup names the popup, and a new
 * quote needs no entry: its address is under the send history already.
 */
const DRAWN_OVER: Readonly<Record<string, string>> = {
  '/signing/new': '/quotes',
};

/** The screen someone at `address` has in front of them, popup or not. */
const screenAt = (address: string): string => DRAWN_OVER[address] ?? address;

/**
 * What the app bar calls the screen you are standing on. Longest match wins, so
 * a screen added later falls back to the app name rather than an unresolved key.
 *
 * Over a create popup it is the popup: the sheet hides the list it opens over,
 * and "new quote" and "new document to sign" are what the sender is doing. The
 * two read alike (4.10); the new document was named after the signing list.
 */
const PAGE_TITLES: Array<[string, string]> = [
  ['/quotes/new', 'quotes.new'],
  ['/signing/new', 'signing.new'],
  ['/mini-site', 'shell.navGallery'],
  ['/inquiries', 'shell.navLeads'],
  ['/calendar', 'calendar.title'],
  ['/quotes', 'quotes.title'],
  ['/signing', 'signing.title'],
  ['/reminders', 'nav.reminders'],
  ['/invoices', 'shell.navInvoicesEmail'],
  ['/customer-payments', 'customerPayments.title'],
  ['/calls', 'calls.title'],
  ['/reports', 'reports.title'],
  ['/profile', 'shell.navPersonalArea'],
  ['/activity', 'nav.activity'],
  ['/platform', 'nav.platform'],
  ['/', 'nav.dashboard'],
];

export function pageTitleKey(pathname: string): string {
  if (pathname === '/quotes') return 'shell.sendHistory';
  // The editor is reached at two unrelated paths and is the one worth naming
  // precisely: "quotes" over a half-filled form does not say whether it is a new
  // quote or an old one being changed.
  if (pathname.startsWith('/quotes/') && pathname.endsWith('/edit')) return 'quotes.edit';

  const match = PAGE_TITLES.find(
    ([prefix]) => pathname === prefix || (prefix !== '/' && pathname.startsWith(`${prefix}/`)),
  );
  // Nothing matched, so the catch-all is about to render. Naming the app here
  // put "Worki" next to the brand and told the reader nothing.
  return match?.[1] ?? 'errors.notFound';
}

export function isActivePath(address: string, item: ShellNavItem): boolean {
  const pathname = screenAt(address);
  // A row may point at one slice of a screen — `/quotes?tab=pending`. The query
  // chooses which tab opens, not which screen you are standing on, so it plays
  // no part in deciding whether this is the row you are on.
  const [path = item.to] = item.to.split('?');
  if (item.end || path === '/') return pathname === path;
  return pathname === path || pathname.startsWith(`${path}/`);
}
